import { isPrimaryPhotoSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import {
  CSSProperties,
  Fragment,
  ReactElement,
  cloneElement,
  isValidElement,
  useMemo,
} from 'react';
import { useWeddingWebsiteData } from '../../data/data-context';
import {
  SectionEntry,
  arrangeSections,
  buildContentSections,
} from '../../sections/arrange-sections';
import { MotifHero } from '../sections/motif-hero';
import { MotifPhoto } from '../sections/motif-photo';
import { MOTIF_SECTION_COMPONENTS } from '../sections/motif-section-components';
import { MOTIF_SECTION_IDS, MotifSectionHookProps } from '../sections/motif-section-hooks';
import { MotifFooter } from './motif-footer';
import { MotifNav } from './motif-nav';

/**
 * Static section orchestrator for the Motif layout. The hero renders separately and owns photo 0; photo 0 is
 * excluded from the interleaved pool so it isn't duplicated under the hero (see the arrangement logic below).
 *
 * UNIVERSAL HOOK-RICH DOM (v2): one markup for every theme. `.bb-ww__wrapper` holds an opt-in `.bb-ww__nav`, the
 * hero, and `.bb-ww__main` (content sections + the always-visible `.bb-ww__footer`, `display: contents` so the
 * layout stays 100% CSS-driven). Each section gets render-order hooks (`--i`, `data-num`, anchor `id`s) injected
 * via cloneElement. See layout-motif/docs/bem-contract.md.
 */
const INTERLEAVED_PHOTO_INDICES = [1, 2];

export const MotifSections = () => {
  const { website } = useWeddingWebsiteData();
  const photosSection = website.sections.find(isPrimaryPhotoSection);

  // Reorderable content sections only — the hero is rendered separately (below), so photo interleaving starts after it.
  const contentSections = useMemo(
    () => buildContentSections(website.sections, { registry: MOTIF_SECTION_COMPONENTS }),
    [website.sections],
  );

  const visibleContentSections = contentSections.filter((section) => section.visible);

  // Photo 0 is the hero image, so the interleaved gallery uses the remaining slots only.
  const photoSections: SectionEntry[] = INTERLEAVED_PHOTO_INDICES.map((index) => ({
    key: `photo-${index}`,
    kind: 'photo',
    visible: !!photosSection?.data[index]?.path,
    element: <MotifPhoto index={index} />,
  }));

  // The hero already renders the couple's first photo (PRIMARY_PHOTOS[0]). The shared arranger places the first
  // interleaved photo *before* the first content section, which — under a hero that owns a large photo — stacks two
  // photos with no content between them. So when the hero has a photo, lead with the first content section and let the
  // arranger interleave the remaining photos through the rest (hero photo → section → photo → section → photo …).
  // No hero photo → keep the default arrangement (a photo-led page is fine there). Motif-only; the shared helper and
  // the Primary layout are untouched.
  const heroHasPhoto = !!photosSection?.data[0]?.path;
  const arranged =
    heroHasPhoto && visibleContentSections.length > 0
      ? [
          visibleContentSections[0],
          ...arrangeSections(visibleContentSections.slice(1), photoSections),
        ]
      : arrangeSections(visibleContentSections, photoSections);

  const orderedVisibleSections = arranged.filter((section) => section.visible);

  // Inject the render-order hooks onto each section's root element (the components spread them onto <section>).
  let contentOrdinal = 0;
  const sectionElements = orderedVisibleSections.map((section, index) => {
    const hookProps: MotifSectionHookProps = {
      style: { '--i': index + 1 } as CSSProperties,
    };
    if (section.kind !== 'photo') {
      contentOrdinal += 1;
      hookProps['data-num'] = String(contentOrdinal).padStart(2, '0');
      const anchorId = MOTIF_SECTION_IDS[section.key];
      if (anchorId) hookProps.id = anchorId;
    }
    return (
      <Fragment key={section.key}>
        {isValidElement(section.element)
          ? cloneElement(section.element as ReactElement<MotifSectionHookProps>, hookProps)
          : section.element}
      </Fragment>
    );
  });

  const photoCount = photoSections.filter((photo) => photo.visible).length;

  return (
    <div className="bb-ww__wrapper">
      <MotifNav sectionKeys={visibleContentSections.map((section) => section.key)} />
      <MotifHero />
      <div
        className="bb-ww__main"
        style={
          {
            '--section-count': orderedVisibleSections.length,
            '--photo-count': photoCount,
          } as CSSProperties
        }>
        {sectionElements}
        <MotifFooter />
      </div>
    </div>
  );
};
